import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import {
  homeSearch,
  parseLegacyRoute,
  profileSearch,
  searchSearch,
} from '../lib/route';
import { ProfilePage } from '../features/profile/ProfilePage';
import { SearchPage } from '../features/search/SearchPage';
import { dashboardExperimentEnabled } from '../features/home/experiment';
import { SearchForm } from '../ui/SearchForm';
import { useAuth } from '../auth/AuthContext';
import { referralForNextPageView, trackSiteEvent } from '../data/site-services';
import { openSupportDialog } from '../data/support';
import {
  navigationPromotionFor,
  type NavigationPromotion,
} from './navigation-promotions';

interface FaqItem {
  question: string;
  answer: string;
}

export function NavigationPromotionIcon({ kind }: { kind: NavigationPromotion['kind'] }) {
  return kind === 'discord'
    ? <img src="../discord.svg" alt="" className="link-icon" />
    : null;
}

const SettingsDialog = lazy(() => import('../features/settings/SettingsDialog'));
const SupportDialog = lazy(() => import('../features/support/SupportDialog'));
const HomePage = lazy(() => import('../features/home/HomePage').then(module => ({
  default: module.HomePage,
})));
const HomeAttentionBadge = lazy(() => import('../features/home/HomeAttentionBadge').then(module => ({
  default: module.HomeAttentionBadge,
})));

function ApplicationView() {
  const location = useLocation();
  const history = useHistory();
  const route = parseLegacyRoute(location.search, location.hash);
  const analyticsRouteKey = route.type === 'profile'
    ? `profile:${route.handle}`
    : route.type === 'search'
      ? `search:${route.searchType}:${route.query}:${route.onlyInterests}`
      : route.type === 'home'
        ? `home:${route.section}`
      : route.type;
  useEffect(() => {
    trackSiteEvent('page_view', { referrer: referralForNextPageView() });
    if (route.type === 'profile') {
      trackSiteEvent('profile_view', { profile: route.handle });
    } else if (route.type === 'search') {
      trackSiteEvent('search');
    }
  }, [analyticsRouteKey]);

  useEffect(() => {
    if (route.type === 'profile' && route.canonicalize) {
      history.replace({ pathname: location.pathname, search: profileSearch(route.handle) });
    }
  }, [history, location.pathname, route]);

  if (route.type === 'oauth-callback') {
    return (
      <section className="classic-state-panel" role="status">
        <h1>Finishing your SkySpace sign-in…</h1>
        <p>You’ll return to your page automatically.</p>
      </section>
    );
  }
  if (route.type === 'search') {
    return (
      <SearchPage
        query={route.query}
        onlyInterests={route.onlyInterests}
        searchType={route.searchType}
      />
    );
  }
  if (route.type === 'home') {
    return (
      <Suspense fallback={(
        <section className="classic-state-panel" role="status">
          <h1>Opening your SkySpace home…</h1>
        </section>
      )}>
        <HomePage section={route.section} />
      </Suspense>
    );
  }
  return <ProfilePage handle={route.handle} />;
}

export function LoginWidget() {
  const auth = useAuth();
  const location = useLocation();
  const [handle, setHandle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const restoringSession = auth.pending && rememberedSession();

  if (auth.handle) {
    return (
      <div className="modern-session-widget">
        <button type="button" onClick={() => void auth.logout()}>Log Out</button>
        <Link
          to={{ pathname: location.pathname, search: profileSearch(auth.handle) }}
          aria-label={`Open @${auth.handle}`}
        >
          <img src={auth.avatar || '../placeholder.png'} alt="" className="modern-session-avatar" />
        </Link>
      </div>
    );
  }
  if (restoringSession) {
    return (
      <div className="modern-session-widget modern-header-session-skeleton" aria-hidden="true">
        <span />
        <span className="modern-session-avatar" />
      </div>
    );
  }
  return (
    <>
      <div className="login-prompt">
        <span className="login-text">Log in here</span>
        <span className="pulse-arrow" />
      </div>
      <form id="login-box" className="modern-login-form" onSubmit={event => {
        event.preventDefault();
        setSubmitting(true);
        void auth.login(handle).catch(() => setSubmitting(false));
      }}>
        <label className="sr-only" htmlFor="bsky-handle">Bluesky handle</label>
        <input
          id="bsky-handle"
          name="username"
          value={handle}
          onChange={event => setHandle(event.target.value)}
          placeholder="Your Bluesky handle"
          autoComplete="username"
        />
        <button
          id="oauth-login-button"
          type="submit"
          disabled={submitting || auth.pending}
          aria-label={submitting ? 'Opening login' : 'Log In'}
        >
          <img src="../editbutton.png" alt="" className="login-image" />
        </button>
        {auth.error && <span className="sr-only" role="alert">{auth.error}</span>}
      </form>
    </>
  );
}

function rememberedSession(): boolean {
  try {
    return Boolean(localStorage.getItem('bskyHandle'));
  } catch {
    return false;
  }
}

function HeaderSearchSkeleton() {
  return (
    <div className="search-container modern-header-search-skeleton" aria-hidden="true">
      <span className="is-radio" />
      <span className="is-radio" />
      <span className="is-input" />
      <span className="is-button" />
    </div>
  );
}

function FaqDialog({ close }: { close: () => void }) {
  const [items, setItems] = useState<FaqItem[]>([]);

  useEffect(() => {
    fetch('../faq.json', { cache: 'no-store' })
      .then(response => response.ok ? response.json() : [])
      .then(value => setItems(Array.isArray(value) ? value : []))
      .catch(() => setItems([]));
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  return (
    <div className="faq-overlay" role="presentation" onMouseDown={event => {
      if (event.currentTarget === event.target) close();
    }}>
      <section className="faq-content" role="dialog" aria-modal="true" aria-labelledby="faq-title">
        <button type="button" className="classic-close" onClick={close} aria-label="Close FAQ">
          ×
        </button>
        <h2 id="faq-title">SkySpace Frequently Asked Questions</h2>
        {items.length === 0 && <p>Loading questions…</p>}
        {items.map(item => (
          <div className="faq-item" key={item.question}>
            <p>{item.question}</p>
            <p dangerouslySetInnerHTML={{ __html: item.answer }} />
          </div>
        ))}
      </section>
    </div>
  );
}

export function App() {
  const location = useLocation();
  const [faqOpen, setFaqOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const supportResult = new URLSearchParams(location.search).get('support');
  const [supportOpen, setSupportOpen] = useState(
    supportResult === 'success' || supportResult === 'cancelled',
  );
  const [navigationPromotion] = useState(() => navigationPromotionFor());
  const auth = useAuth();
  const restoringSession = auth.pending && rememberedSession();
  useEffect(() => {
    const open = () => setSupportOpen(true);
    window.addEventListener('skyspace:open-support', open);
    return () => window.removeEventListener('skyspace:open-support', open);
  }, []);

  return (
    <div id="page-content">
      <div className="page-container">
        <header className="header" id="page-header">
          <Link
            to={{ pathname: location.pathname, search: profileSearch('skyspace.me') }}
            aria-label="SkySpace home"
          >
            <img src="../skyspacelogo.png" alt="SkySpace Logo" className="logo" />
          </Link>

          {auth.handle ? <SearchForm /> : restoringSession ? <HeaderSearchSkeleton /> : null}

          <div className="login-container">
            <LoginWidget />
          </div>
        </header>

        <nav className="link-bar" aria-label="Primary navigation">
          <span className="group1">
            <Link
              to={{
                pathname: location.pathname,
                search: auth.handle && dashboardExperimentEnabled()
                  ? homeSearch('feed')
                  : profileSearch('skyspace.me'),
              }}
            >
              <img src="../linkbar-home.png" alt="" className="link-icon" />
              Home
              {auth.handle && dashboardExperimentEnabled() && (
                <Suspense fallback={null}>
                  <HomeAttentionBadge />
                </Suspense>
              )}
            </Link>
            <span className="separator">|</span>
            {auth.handle && (
              <span id="my-page">
                <Link to={{ pathname: location.pathname, search: profileSearch(auth.handle) }}>
                  <img src="../linkbar-mypage.png" alt="" className="link-icon" />
                  My Page
                </Link>
                <span className="separator">|</span>
              </span>
            )}
            <Link to={{ pathname: location.pathname, search: searchSearch('') }}>
              <img src="../linkbar-latestusers.png" alt="" className="link-icon" />
              Latest People!
            </Link>
          </span>
          <span className="group2">
            <span className="separator">|</span>
            <a
              href={navigationPromotion.href}
              data-nav-promo={navigationPromotion.kind}
            >
              <NavigationPromotionIcon kind={navigationPromotion.kind} />
              {navigationPromotion.label}
            </a>
            <span className="separator">|</span>
            <button className="link-bar-button" type="button" onClick={openSupportDialog}>
              <img src="../linkbar-supportskyspace.png" alt="" className="link-icon" />
              Support SkySpace
            </button>
            <span className="separator">|</span>
            <span className="modern-help-settings">
              <button className="link-bar-button" type="button" onClick={() => setFaqOpen(true)}>
                <img src="../linkbar-faq.png" alt="" className="link-icon" />
                FAQ
              </button>
              <span className="separator">|</span>
              <button className="link-bar-button" type="button" onClick={() => {
                trackSiteEvent('settings_open');
                setSettingsOpen(true);
              }}>
                <svg
                  className="link-icon modern-settings-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M19.43 12.98c.04-.32.07-.65.07-.98s-.03-.66-.07-.98l2.11-1.65a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.6-.22l-2.49 1a7.4 7.4 0 0 0-1.69-.98l-.38-2.65A.5.5 0 0 0 14 2h-4a.5.5 0 0 0-.5.42l-.38 2.65c-.61.25-1.17.58-1.69.98l-2.49-1a.5.5 0 0 0-.6.22l-2 3.46a.5.5 0 0 0 .12.64l2.11 1.65a7.2 7.2 0 0 0 0 1.96l-2.11 1.65a.5.5 0 0 0-.12.64l2 3.46a.5.5 0 0 0 .6.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65A.5.5 0 0 0 10 22h4a.5.5 0 0 0 .5-.42l.38-2.65c.61-.25 1.17-.58 1.69-.98l2.49 1a.5.5 0 0 0 .6-.22l2-3.46a.5.5 0 0 0-.12-.64l-2.11-1.65ZM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Z"
                  />
                </svg>
                Settings
              </button>
            </span>
          </span>
        </nav>

        <main>
          <Switch>
            <Route path="*">
              <ApplicationView />
            </Route>
          </Switch>
        </main>

        <footer className="footer">
          <p>
            © 2026 Ste Curran / One Life Left Ltd. Is it really copyright if
            you&apos;re just copying in the first place? Anyway, here we are.
            All rights reserved.
          </p>
        </footer>
      </div>
      {faqOpen && <FaqDialog close={() => setFaqOpen(false)} />}
      {settingsOpen && (
        <Suspense fallback={null}>
          <SettingsDialog close={() => setSettingsOpen(false)} />
        </Suspense>
      )}
      {supportOpen && (
        <Suspense fallback={null}>
          <SupportDialog
            close={() => setSupportOpen(false)}
            checkoutResult={
              supportResult === 'success' || supportResult === 'cancelled'
                ? supportResult
                : undefined
            }
          />
        </Suspense>
      )}
    </div>
  );
}
